import { useState, useEffect } from "react"
import { BarChart3, Percent, Building2, Link, Edit, Save, X, AlertCircle } from "lucide-react"
import { FMOService } from "@/features/fmo"
import { OrganizationService } from "@/features/organizations"
import { IntakeLinkAssignmentService } from "@/features/fmo"
import { LoadBalancerService, LoadBalancerConfiguration } from "@/features/fmo"
import { VendorIntakeForm } from "@/features/campaigns"
import type { Organization } from "@/features/organizations"
import { useCampaign } from "@/features/campaigns"

interface LoadBalanceIntakeLinksProps {
  campaignName?: string
}

interface IntakeLink {
  id: string
  alias: string
  lawFirmName: string
  intakeFormUrl: string
}

interface OrganizationWithAssignments {
  id: string
  name: string
  type: 'EM' | 'DM' | 'CM' | 'BM' | 'AM'
  assignedLinks: {
    linkId: string
    percentage: number
  }[]
}

export default function LoadBalanceIntakeLinks({ campaignName }: LoadBalanceIntakeLinksProps) {
  const { selectedCampaign } = useCampaign()
  
  // State for real data
  const [intakeLinks, setIntakeLinks] = useState<IntakeLink[]>([])
  const [organizations, setOrganizations] = useState<OrganizationWithAssignments[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  const [editingOrg, setEditingOrg] = useState<string | null>(null)
  const [tempPercentages, setTempPercentages] = useState<{[key: string]: number}>({})
  const [savingOrg, setSavingOrg] = useState<string | null>(null)

  // Fetch data on component mount and when selectedCampaign changes
  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if no campaign is selected
      if (!selectedCampaign) {
        setIntakeLinks([])
        setOrganizations([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        // Fetch intake links, existing assignments, and load balancer configurations in parallel
        const [vendorIntakeForms, existingAssignments, loadBalancerConfigs] = await Promise.all([
          FMOService.getVendorIntakeLinks(),
          IntakeLinkAssignmentService.getAssignments().catch(() => []), // Don't fail if no assignments exist
          LoadBalancerService.getConfigurations().catch(() => []) // Don't fail if no configs exist
        ])
        
        // CRITICAL: Filter intake links by selectedCampaign.id
        const campaignFilteredIntakeForms = vendorIntakeForms.filter(
          (form: VendorIntakeForm) => form.campaignId === selectedCampaign.id
        )
        
        // Transform intake links (only for selected campaign)
        const transformedLinks: IntakeLink[] = campaignFilteredIntakeForms.map((form: VendorIntakeForm) => ({
          id: form.id,
          alias: `${form.vendorName} - ${form.businessType}`,
          lawFirmName: form.vendorName,
          intakeFormUrl: form.email
        }))
        setIntakeLinks(transformedLinks)
        
        // Get the IDs of filtered intake links for this campaign
        const campaignIntakeLinkIds = new Set(transformedLinks.map(link => link.id))
        
        // Fetch all organizations by level
        const [emOrgs, dmOrgs, cmOrgs, bmOrgs, amOrgs] = await Promise.all([
          OrganizationService.getOrganizationsByLevel('EM'),
          OrganizationService.getOrganizationsByLevel('DM'),
          OrganizationService.getOrganizationsByLevel('CM'),
          OrganizationService.getOrganizationsByLevel('BM'),
          OrganizationService.getOrganizationsByLevel('AM')
        ])
        
        // Transform organizations and merge with existing assignments
        const allOrgs = [
          ...emOrgs.map(org => ({ ...org, type: 'EM' as const })),
          ...dmOrgs.map(org => ({ ...org, type: 'DM' as const })),
          ...cmOrgs.map(org => ({ ...org, type: 'CM' as const })),
          ...bmOrgs.map(org => ({ ...org, type: 'BM' as const })),
          ...amOrgs.map(org => ({ ...org, type: 'AM' as const }))
        ]
        
        // Create organizations with their assigned links and load balancer configurations
        const orgsWithAssignments: OrganizationWithAssignments[] = allOrgs.map(org => {
          const assignedLinks: { linkId: string; percentage: number }[] = []
          
          // Find assignments for this organization
          if (existingAssignments && existingAssignments.length > 0) {
            existingAssignments.forEach(assignment => {
              // CRITICAL: Only include assignments for intake links in this campaign
              if (!campaignIntakeLinkIds.has(assignment.id)) {
                return // Skip intake links not in selected campaign
              }
              
              const hasAssignment = 
                assignment.emAssignments?.some(m => m.id === org.id) ||
                assignment.dmAssignments?.some(m => m.id === org.id) ||
                assignment.cmAssignments?.some(m => m.id === org.id) ||
                assignment.bmAssignments?.some(m => m.id === org.id) ||
                assignment.amAssignments?.some(m => m.id === org.id)
              
              if (hasAssignment) {
                // Check if there's a load balancer configuration for this organization and link
                const loadBalancerConfig = loadBalancerConfigs.find(config => 
                  config.id === org.id && 
                  config.assignedLinks.some(link => link.linkId === assignment.id)
                )
                
                const percentage = loadBalancerConfig 
                  ? loadBalancerConfig.assignedLinks.find(link => link.linkId === assignment.id)?.percentage || 0
                  : 0
                
                assignedLinks.push({
                  linkId: assignment.id,
                  percentage
                })
              }
            })
          }
          
          return {
            id: org.id,
            name: org.name,
            type: org.type,
            assignedLinks
          }
        })
        
        // CRITICAL: Only show organizations that have assigned links for this campaign
        const orgsWithCampaignLinks = orgsWithAssignments.filter(
          org => org.assignedLinks.length > 0
        )
        
        setOrganizations(orgsWithCampaignLinks)
      } catch (err) {
        console.error('Failed to fetch data:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [selectedCampaign]) // Re-fetch when selectedCampaign changes

  // Get link details by ID
  const getLinkById = (linkId: string) => {
    return intakeLinks.find(link => link.id === linkId)
  }

  // Calculate total percentage for an organization
  const getTotalPercentage = (org: OrganizationWithAssignments) => {
    return org.assignedLinks.reduce((total, link) => total + link.percentage, 0)
  }

  // Start editing an organization
  const startEditing = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId)
    if (org) {
      const percentages: {[key: string]: number} = {}
      org.assignedLinks.forEach(link => {
        percentages[link.linkId] = link.percentage
      })
      setTempPercentages(percentages)
      setEditingOrg(orgId)
    }
  }

  // Save changes
  const saveChanges = async (orgId: string) => {
    
    try {
      setSavingOrg(orgId)
      setSaveError(null)
      
      // Update local state first
      const updatedOrg = organizations.find(org => org.id === orgId)
      if (!updatedOrg) return
      
      const updatedAssignedLinks = updatedOrg.assignedLinks.map(link => ({
        ...link,
        percentage: tempPercentages[link.linkId] || 0
      }))
      
      // Prepare configurations for this specific organization
      const configurations: LoadBalancerConfiguration[] = updatedAssignedLinks.map(link => ({
        organizationId: orgId,
        vendorIntakeId: link.linkId,
        percentage: link.percentage
      }))
      
      
      // Save to backend
      await LoadBalancerService.saveConfigurations(configurations)
      
      // Update local state after successful save
      setOrganizations(prev => prev.map(org => {
        if (org.id === orgId) {
          return {
            ...org,
            assignedLinks: updatedAssignedLinks
          }
        }
        return org
      }))
      
      
    } catch (err) {
      console.error('Failed to save organization configuration:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save organization configuration')
    } finally {
      setSavingOrg(null)
    }
    
    setEditingOrg(null)
    setTempPercentages({})
  }

  // Cancel editing
  const cancelEditing = () => {
    setEditingOrg(null)
    setTempPercentages({})
  }

  // Update percentage
  const updatePercentage = (linkId: string, percentage: number) => {
    const clampedPercentage = Math.max(0, Math.min(100, percentage))
    
    setTempPercentages(prev => {
      const newTempPercentages = {
        ...prev,
        [linkId]: clampedPercentage
      }
      return newTempPercentages
    })
  }

  // Save load balancer configurations
  const saveLoadBalancerConfigurations = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setSaveSuccess(false)
      
      // Prepare configurations for all organizations
      const configurations: LoadBalancerConfiguration[] = []
      
      
      organizations.forEach(org => {
        org.assignedLinks.forEach(link => {
          configurations.push({
            organizationId: org.id,
            vendorIntakeId: link.linkId,
            percentage: link.percentage
          })
        })
      })
      
      
      await LoadBalancerService.saveConfigurations(configurations)
      
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      
    } catch (err) {
      console.error('Failed to save load balancer configurations:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save load balancer configurations')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Main Settings Container */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
        
        {/* Top Row - Load Balance Intake Links */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-8 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Load Balance Intake Links</h2>
                <p className="text-gray-600">Configure percentage splits across multiple URLs per organization for {campaignName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-orange-800 whitespace-nowrap">Load Balancer</span>
              </div>
              
              {/* Selected Campaign Display */}
              {selectedCampaign && (
                <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-xl">
                  <span className="text-sm font-semibold text-primary-900 whitespace-nowrap">{selectedCampaign.name}</span>
                </div>
              )}
              
              {/* Save Button */}
              <button
                onClick={saveLoadBalancerConfigurations}
                disabled={saving || !selectedCampaign}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap ${
                  saving || !selectedCampaign
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : saveSuccess
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 bg-white/30 rounded animate-pulse"></div>
                    <span>Saving...</span>
                  </>
                ) : saveSuccess ? (
                  <>
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span>Saved!</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Configurations</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Error Alert */}
        {saveError && (
          <div className="mx-8 mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-3 h-3 text-red-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-red-800">Save Failed</div>
                  <div className="text-xs text-red-600">{saveError}</div>
                </div>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* No Campaign Selected Warning */}
        {!selectedCampaign && (
          <div className="mx-8 mt-6 mb-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-800">No Campaign Selected</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Please select a campaign from the dropdown above to view and configure load balance settings.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Load Balance Configuration */}
        <div className="p-8">
          {loading ? (
            // Shimmer loading state
            <div className="space-y-6">
              {/* Instructions Shimmer */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-48"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
              </div>

              {/* Organizations Shimmer */}
              <div className="space-y-4">
                {[1, 2, 3, 4].map((index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                    {/* Organization Header Shimmer */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                        <div className="h-6 bg-gray-200 rounded animate-pulse w-32"></div>
                        <div className="h-6 bg-gray-200 rounded-full animate-pulse w-12"></div>
                        <div className="h-6 bg-gray-200 rounded-full animate-pulse w-16"></div>
                      </div>
                      <div className="h-8 bg-gray-200 rounded-lg animate-pulse w-24"></div>
                    </div>

                    {/* Assigned Links Shimmer */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {[1, 2, 3, 4].map((linkIndex) => (
                        <div key={linkIndex} className="bg-white rounded-lg p-4 border-2 border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-gray-200 rounded-lg animate-pulse"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                          </div>
                          <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                            <div className="flex items-center gap-2">
                              <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
                              <div className="h-6 bg-gray-200 rounded animate-pulse w-12"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Status Indicator Shimmer */}
                    <div className="mt-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Shimmer */}
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                <div className="h-6 bg-gray-200 rounded animate-pulse w-48 mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="h-8 bg-gray-200 rounded animate-pulse w-12 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : error ? (
            // Error state
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 mb-2">Failed to load data</div>
                <div className="text-sm text-gray-500">{error}</div>
              </div>
            </div>
          ) : !selectedCampaign ? (
            // Show nothing if no campaign selected (warning already shown above)
            null
          ) : organizations.length === 0 ? (
            // Empty state for selected campaign with no organizations
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-gray-500">
                <div className="mb-2">No organizations with intake links for this campaign</div>
                <div className="text-sm">Assign intake links to organizations to enable load balancing</div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
            
            {/* Instructions */}
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Percent className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Fulfillment Load Balancer</h3>
              </div>
              <p className="text-gray-700 text-sm">
                Configure percentage splits across multiple URLs per organization. For example: 60% to URL A, 30% to URL B, 10% to URL C.
                The total percentage for each organization should equal 100%.
              </p>
            </div>

            {/* Organizations and their Link Assignments */}
            <div className="space-y-4">
              {organizations.map((org) => (
                <div key={org.id} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                  
                  {/* Organization Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                      <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {org.type}
                      </div>
                      <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        Total: {getTotalPercentage(org)}%
                      </div>
                    </div>
                    
                    {editingOrg === org.id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveChanges(org.id)}
                          disabled={savingOrg === org.id}
                          className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
                            savingOrg === org.id
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {savingOrg === org.id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium"
                        >
                          <X className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(org.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium"
                      >
                        <Edit className="w-4 h-4" />
                        Configure
                      </button>
                    )}
                  </div>

                  {/* Assigned Links */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {org.assignedLinks.map((assignedLink, index) => {
                      const link = getLinkById(assignedLink.linkId)
                      if (!link) return null
                      
                      return (
                        <div key={assignedLink.linkId} className="bg-white rounded-lg p-4 border-2 border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Link className="w-3 h-3 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">Link {assignedLink.linkId}</span>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-gray-900">{link.alias}</div>
                            <div className="text-xs text-gray-500">{link.lawFirmName}</div>
                            
                            {/* Percentage Input */}
                            <div className="flex items-center gap-2">
                              <label className="text-xs font-medium text-gray-700">Percentage:</label>
                              {editingOrg === org.id ? (
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={tempPercentages[assignedLink.linkId] || assignedLink.percentage}
                                  onChange={(e) => updatePercentage(assignedLink.linkId, parseInt(e.target.value) || 0)}
                                  className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              ) : (
                                <span className="text-sm font-semibold text-blue-600">
                                  {assignedLink.percentage}%
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    
                    {/* Empty slots for more links */}
                    {org.assignedLinks.length < 4 && (
                      <>
                        {Array.from({ length: 4 - org.assignedLinks.length }).map((_, index) => (
                          <div key={`empty-${index}`} className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center">
                            <span className="text-gray-400 text-sm">Empty slot</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Status Indicator */}
                  <div className="mt-4 flex items-center gap-2">
                    {getTotalPercentage(org) === 100 ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Perfectly balanced (100%)</span>
                      </div>
                    ) : getTotalPercentage(org) > 100 ? (
                      <div className="flex items-center gap-2 text-red-600">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">Over-allocated ({getTotalPercentage(org)}%)</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600">
                        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                        <span className="text-sm font-medium">Under-allocated ({getTotalPercentage(org)}%)</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Load Balance Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-2xl font-bold text-green-600">
                    {organizations.filter(org => getTotalPercentage(org) === 100).length}
                  </div>
                  <div className="text-sm text-gray-600">Perfectly Balanced</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-2xl font-bold text-orange-600">
                    {organizations.filter(org => getTotalPercentage(org) < 100 && getTotalPercentage(org) > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">Under-allocated</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="text-2xl font-bold text-red-600">
                    {organizations.filter(org => getTotalPercentage(org) > 100).length}
                  </div>
                  <div className="text-sm text-gray-600">Over-allocated</div>
                </div>
              </div>
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
